import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import { useAuth } from '../../../context/AuthContext';
import { getIndustrialCostContext } from '../services/industrialCostService';

const ALLOWED_INDUSTRIAL_COST_ROLES = new Set(['admin', 'factory_manager']);

const IndustrialCostAccessDenied = () => (
  <Box
    sx={{
      minHeight: '100vh',
      bgcolor: '#020617',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 4,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 520,
        borderRadius: 4,
        border: '1px solid rgba(248,113,113,0.24)',
        bgcolor: '#0f172a',
        p: { xs: 3, sm: 4 },
      }}
    >
      <Stack spacing={2.5} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            bgcolor: 'rgba(56,189,248,0.12)',
            color: '#38bdf8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(56,189,248,0.24)',
          }}
        >
          <FactoryRoundedIcon />
        </Box>

        <Stack spacing={0.8}>
          <Typography sx={{ color: '#f8fafc', fontSize: '1.35rem', fontWeight: 950 }}>
            Acceso industrial no disponible
          </Typography>
          <Typography sx={{ color: '#94a3b8' }}>
            Tu usuario no tiene permisos para acceder a PROA Industrial Cost System.
          </Typography>
        </Stack>

        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            width: '100%',
            borderColor: 'rgba(251,191,36,0.35)',
            color: '#fde68a',
            '& .MuiAlert-icon': { color: '#fbbf24' },
          }}
        >
          <AlertTitle>Permiso requerido</AlertTitle>
          Solicita al administrador del tenant que te asigne el rol industrial correspondiente.
        </Alert>

        <Button
          variant="outlined"
          onClick={() => {
            window.location.href = '/industrial-cost/login';
          }}
        >
          Volver al acceso industrial
        </Button>
      </Stack>
    </Paper>
  </Box>
);

const IndustrialCostProtectedRoute = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  const industrialContextQuery = useQuery({
    queryKey: ['industrial-cost-context'],
    queryFn: getIndustrialCostContext,
    enabled: Boolean(user),
    retry: false,
  });

  if (isLoading || (user && industrialContextQuery.isLoading)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#020617',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/industrial-cost/login" state={{ from: location }} replace />;
  }

  const role =
    industrialContextQuery.data?.role ?? industrialContextQuery.data?.industrial_costs_role;

  if (role === 'factory_operator') {
    return <Navigate to="/factory/operator" replace />;
  }

  if (!role || !ALLOWED_INDUSTRIAL_COST_ROLES.has(role)) {
    return <IndustrialCostAccessDenied />;
  }

  return <Outlet />;
};

export default IndustrialCostProtectedRoute;